import { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../auth-lib';
import AdminLayout from '../components/AdminLayout';
import { Button, Card, Input, EmptyState, Skeleton, StatusPill, useToast } from '../../design-system';
import '../styles/AdminPortals.css';

// Super-admin view of the GLOBAL portal catalog (the login flows staff can store
// client credentials against). Names, URLs and login recipes are code-owned
// (src/recipes, applied by `npm run seed:portals`); the one thing editable here
// is whether a portal is active. "Verified" is derived from selectorVersion — a
// recipe at v1 is the unverified baseline until its selectors are checked against
// the live page and the version is bumped.
function AdminPortals() {
  const [portals, setPortals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [togglingId, setTogglingId] = useState(null);
  const toast = useToast();

  useEffect(() => {
    fetchPortals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPortals = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get('/api/portals/admin');
      setPortals(res.data);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to load portals');
    } finally {
      setLoading(false);
    }
  };

  const totals = useMemo(
    () => ({
      all: portals.length,
      verified: portals.filter((p) => p.verified).length,
      unverified: portals.filter((p) => p.hasRecipe && !p.verified).length,
      disabled: portals.filter((p) => !p.isActive).length,
    }),
    [portals]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return portals;
    return portals.filter(
      (p) => p.code.toLowerCase().includes(term) || p.displayName.toLowerCase().includes(term)
    );
  }, [portals, search]);

  const toggleActive = async (p) => {
    const next = !p.isActive;
    if (
      !next &&
      !window.confirm(
        `Disable "${p.displayName}"? It disappears from every organization's credential picker immediately. Stored logins stay intact.`
      )
    ) {
      return;
    }
    setTogglingId(p._id);
    try {
      const res = await apiClient.put(`/api/portals/admin/${p._id}/active`, { isActive: next });
      setPortals((prev) => prev.map((x) => (x._id === p._id ? res.data : x)));
      toast.success(`${p.displayName} ${next ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to update portal');
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <AdminLayout
      title="Portal Catalog"
      subtitle="The global auto-login catalog — the portals staff can store client credentials against"
    >
      <div className="admin-portals-page">
        {!loading && (
          <div className="admin-portals-summary">
            <div className="admin-portals-stat">
              <span className="admin-portals-number">{totals.all}</span>
              <span className="admin-portals-label">Portals</span>
            </div>
            <div className="admin-portals-stat">
              <span className="admin-portals-number">{totals.verified}</span>
              <span className="admin-portals-label">Verified</span>
            </div>
            <div className="admin-portals-stat">
              <span className="admin-portals-number">{totals.unverified}</span>
              <span className="admin-portals-label">Unverified</span>
            </div>
            <div className="admin-portals-stat">
              <span className="admin-portals-number">{totals.disabled}</span>
              <span className="admin-portals-label">Disabled</span>
            </div>
          </div>
        )}

        <div className="admin-portals-toolbar">
          <Input
            className="admin-portals-search"
            placeholder="🔎 Search portals…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {loading ? (
          <Card>
            <Skeleton.Text lines={8} />
          </Card>
        ) : filtered.length === 0 ? (
          <EmptyState icon="🔐" title="No portals found" description="Try a different search term." />
        ) : (
          <div className="admin-portals-list">
            {filtered.map((p) => (
              <Card
                key={p._id}
                className={`admin-portal-row ${p.isActive ? '' : 'admin-portal-row-off'}`}
              >
                <div className="admin-portal-identity">
                  <span className="admin-portal-code">{p.code}</span>
                  <div className="admin-portal-meta">
                    <div className="admin-portal-name">{p.displayName}</div>
                    <a
                      className="admin-portal-url"
                      href={p.loginUrl}
                      target="_blank"
                      rel="noreferrer"
                      title={p.loginUrl}
                    >
                      {p.loginUrl}
                    </a>
                  </div>
                </div>

                <div className="admin-portal-badges">
                  <StatusPill
                    variant={p.hasRecipe ? 'success' : 'neutral'}
                    label={p.hasRecipe ? `Auto-login · ${p.stepCount} steps` : 'Store & reveal'}
                  />
                  {p.hasRecipe && (
                    <StatusPill
                      variant={p.verified ? 'success' : 'warning'}
                      label={p.verified ? `Selectors v${p.selectorVersion} · verified` : `Selectors v${p.selectorVersion} · unverified`}
                    />
                  )}
                  {p.requiresCaptcha && <StatusPill variant="info" label="Captcha" />}
                  {p.requiresOtp && <StatusPill variant="info" label="OTP" />}
                  {p.jurisdiction && p.jurisdiction !== 'IN' && (
                    <StatusPill variant="neutral" label={p.jurisdiction} />
                  )}
                  {!p.isActive && <StatusPill variant="danger" label="Disabled" />}
                </div>

                <Button
                  variant={p.isActive ? 'ghost' : 'secondary'}
                  size="sm"
                  loading={togglingId === p._id}
                  onClick={() => toggleActive(p)}
                >
                  {p.isActive ? 'Disable' : 'Enable'}
                </Button>
              </Card>
            ))}
          </div>
        )}

        <p className="admin-portals-note">
          Portals are seeded from the recipe library in <code>src/recipes</code> and refreshed with{' '}
          <code>npm run seed:portals</code>. Names, login URLs and recipes are code-owned; the active
          toggle here is preserved across re-seeds. After checking a recipe&apos;s selectors against the
          live login page, bump its <code>selectorVersion</code> to mark it verified.
        </p>
      </div>
    </AdminLayout>
  );
}

export default AdminPortals;
